import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivityService } from '@gms/activity-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { HttpResponse } from '@angular/common/http';
import { AuthTicket, TicketService } from '@gms/security-api';
import { LoginResponse, MfaChallengeInfo, RolesPageCollection, TokenPair, UserService, MfaService } from '@gms/user-api';
import { SsoService } from 'app/modules/sso/sso.service';
import { IAppState } from 'app/store/app/app.state';
import { EUsersActions } from 'app/store/users/users.actions';
import { appConfig } from 'config/app-config';
import { Observable, of, concat } from 'rxjs';
import { catchError, filter, exhaustMap, map, switchMap, take, withLatestFrom, tap } from 'rxjs/operators';
import { WebsocketService } from 'shared/services/websocket.service';
import {
  EAuthActions,
  FetchMfaOptions,
  FetchMfaOptionsFailure,
  FetchMfaOptionsSuccess,
  FetchRouteConfigs,
  FetchRouteConfigsFailure,
  FetchRouteConfigsSuccess,
  FetchUserFailure,
  FetchUserSuccess,
  FetchWebsocketTicket,
  FetchWebsocketTicketFailure,
  FetchWebsocketTicketSuccess,
  InitiateTotpEnrollment,
  InitiateTotpEnrollmentFailure,
  InitiateTotpEnrollmentSuccess,
  LogIn,
  LogInFailure,
  LogInMfa,
  LogInMfaError,
  LogInSuccess,
  LogOut,
  LogOutWithError,
  RedirectToMfa,
  Refresh,
  RefreshCancel,
  RefreshFailure,
  RefreshSuccess,
  RememberDevice,
  RememberDeviceFailure,
  RememberDeviceSuccess,
  ClearRememberedDevice,
  SelectLoginMfaMethod,
  SelectLoginMfaMethodFailure,
  SelectLoginMfaMethodSuccess,
  SsoLogIn,
  SsoLogInFailure,
  SsoRedirect,
  SwitchMfaMethod,
  SwitchMfaMethodFailure,
  SwitchMfaMethodSuccess,
  UpdatePassword,
  UpdatePasswordFailure,
  UpdatePasswordSuccess,
  VerifyTotpEnrollment,
  VerifyTotpEnrollmentFailure,
  VerifyTotpEnrollmentSuccess,
} from './auth.actions';
import { selectIsADFSLogin, selectIsAuthenticated, selectRefreshToken, selectSessionDeviceKey, selectRememberDevicePrompt, selectTotpProvisioningUri, selectTotpSecretCode, selectTotpSession, selectTotpEnrollAccessToken, selectCognitoAccessToken, selectRememberDeviceData, selectUser, selectMfaOptions } from './auth.selectors';
import { LABELS } from 'shared/consts/labels.const';
import { BISACONSTANTS } from 'app/modules/bisa/bisa-constants';

@Injectable()
export class AuthEffects {
  constructor(
    private _actions$: Actions,
    private _router: Router,
    private _store: Store<IAppState>,
    private _activityService: ActivityService,
    private _ssoService: SsoService,
    private _userService: UserService,
    private _webSocketService: WebsocketService,
    private _ticketService: TicketService,
    private _route: ActivatedRoute,
    private _mfaService: MfaService
  ) { }

  private generateDeviceFingerprint(): string {
    const raw = `${navigator.userAgent}|${screen.width}x${screen.height}|${navigator.language}|${navigator.platform}|${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      hash = (hash * 31 + raw.charCodeAt(i)) % 2147483647;
    }
    return hash.toString(16);
  }

  LogIn: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<LogIn>(EAuthActions.LOG_IN),
      map((action: LogIn) => action.payload),
      withLatestFrom(this._store.pipe(select(selectRememberDeviceData))),
      switchMap(([payload, rememberDevices]) => {
        const hasDeviceCredentials = !!rememberDevices[payload.userId]?.deviceKey && rememberDevices[payload.userId]?.confirmed === true;
        const deviceCredentials = hasDeviceCredentials
          ? {
              deviceKey: rememberDevices[payload.userId].deviceKey,
              deviceGroupKey: rememberDevices[payload.userId].deviceGroupKey,
              devicePassword: rememberDevices[payload.userId].devicePassword,
            }
          : {};

        const buildLoginRequest = (includeDevice: boolean) => ({
          username: payload.userId,
          password: payload.password,
          clientId: appConfig.clientId,
          deviceFingerprint: this.generateDeviceFingerprint(),
          // AC6 (Req 6): raw userAgent forwarded as a standalone risk-context signal;
          // IP address is resolved server-side from the incoming request.
          userAgent: navigator.userAgent,
          ...(includeDevice ? deviceCredentials : {}),
        } as any);

        const handleResponse = (response: HttpResponse<LoginResponse | MfaChallengeInfo>, includeDeviceData: boolean) => {
          if (response.status === 202) {
            return new RedirectToMfa({
              mfaChallengeInfo: {
                ...(response.body as MfaChallengeInfo),
                availableMethods: (response.body as any).availableMethods ?? [],
              },
              userId: String((response.body as any).userId),
              username: payload.userId,
              isBisa: payload.isBisa,
              phoneLastFour: (response.body as any).phoneLastFour ?? null,
              deviceKey: includeDeviceData ? (rememberDevices[payload.userId]?.deviceKey ?? null) : null,
              deviceGroupKey: includeDeviceData ? (rememberDevices[payload.userId]?.deviceGroupKey ?? null) : null,
              devicePassword: includeDeviceData ? (rememberDevices[payload.userId]?.devicePassword ?? null) : null,
            });
          } else {
            const tokenPair = response.body as LoginResponse;
            const deviceMetadata = tokenPair.deviceMetadata;

            return new LogInSuccess({
              userId: payload.userId,
              loginUsername: payload.userId,
              isADFSLogin: false,
              accessToken: tokenPair.idToken,
              cognitoAccessToken: tokenPair.access,
              refreshToken: tokenPair.refresh,
              ttl: tokenPair.ttl,
              roles: tokenPair.roles || [],
              authResources: tokenPair.resources,
              isInternal: tokenPair.isInternal,
              isBisa: payload.isBisa,
              sessionDeviceKey: deviceMetadata?.deviceKey ?? (includeDeviceData ? rememberDevices[payload.userId]?.deviceKey : undefined),
              deviceKey: deviceMetadata?.deviceKey,
              deviceGroupKey: deviceMetadata?.deviceGroupKey,
              devicePassword: deviceMetadata?.devicePassword,
            });
          }
        };

        return this._userService
          .loginUser(buildLoginRequest(hasDeviceCredentials), 'response')
          .pipe(
            map((response: HttpResponse<LoginResponse | MfaChallengeInfo>) => handleResponse(response, hasDeviceCredentials)),
            catchError(error => {
              // If login failed with device credentials, clear the stale device data but do not retry automatically
              if (hasDeviceCredentials) {
                return concat(
                  of(new ClearRememberedDevice({ userId: payload.userId })),
                  of(new LogInFailure({ error }))
                );
              }
              return of(new LogInFailure({ error: error }));
            })
          );
      })
    )
  );

  SsoLogIn: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<SsoLogIn>(EAuthActions.SSO_LOG_IN),
      map((action: SsoLogIn) => action.payload),
      switchMap(payload =>
        this._ssoService
          .loginSsoUser({
            code: payload.code,
          })
          .pipe(
            switchMap(loginResponse =>
              this._userService.loginAdfsUser(
                {
                  idToken: loginResponse.id_token,
                  accessToken: loginResponse.access_token,
                  refreshToken: loginResponse.refresh_token,
                },
                'response'
              )
            ),
            map((response: HttpResponse<TokenPair | MfaChallengeInfo>) => {
              if (response.status === 202) {
                const challenge = response.body as MfaChallengeInfo;
                return new RedirectToMfa({
                  mfaChallengeInfo: challenge,
                  userId: String((response.body as any)?.userId ?? ''),
                  username: (response.body as any)?.email ?? '',
                  isBisa: payload.isbisa ?? false,
                });
              } else {
                const tokenPair = response.body as TokenPair;
                return new LogInSuccess({
                  userId: tokenPair.email,
                  isADFSLogin: true,
                  accessToken: tokenPair.idToken,
                  cognitoAccessToken: tokenPair.access,
                  refreshToken: tokenPair.refresh,
                  ttl: tokenPair.ttl,
                  roles: tokenPair.roles || [],
                  authResources: tokenPair.resources,
                  isInternal: tokenPair.isInternal,
                  isBisa: payload.isbisa
                });
              }
            }),
            catchError(error => of(new SsoLogInFailure({ error: error })))
          )
      )
    )
  );

  LogInMfa: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<LogInMfa>(EAuthActions.LOG_IN_MFA),
      map((action: LogInMfa) => action.payload),
      switchMap(payload =>
        this._userService
          .loginUserMfa({
            username: payload.username ?? payload.userId,
            clientId: appConfig.clientId,
            mfaCode: payload.mfaCode,
            sessionId: payload.sessionId,
            challengeName: payload.challengeName,
            deviceKey: payload.deviceKey,
            deviceGroupKey: payload.deviceGroupKey,
            devicePassword: payload.devicePassword,
            // Backend owns all Cognito device tracking (ConfirmDevice, UpdateDeviceStatus, device key issuance)
          } as any)
          .pipe(
            switchMap((response: LoginResponse) => {
              const metadata = response.deviceMetadata;
              const deviceKey = metadata?.deviceKey;
              const username = payload.username ?? payload.userId;
              // Without response metadata, the key remembered below came from method selection; persist it with its credentials.
              const useFallbackDevice = !deviceKey && !!payload.rememberDevice && !!payload.deviceKey;

              const successAction = new LogInSuccess({
                userId: payload.userId,
                loginUsername: username,
                isADFSLogin: false,
                accessToken: response.idToken,
                cognitoAccessToken: response.access,
                refreshToken: response.refresh,
                ttl: response.ttl,
                roles: response.roles || [],
                authResources: response.resources,
                isInternal: response.isInternal,
                isBisa: payload.isBisa,
                sessionDeviceKey: deviceKey ?? payload.deviceKey,
                // Device is already confirmed server-side, so persist it whether or not the user ticked "remember".
                deviceKey: useFallbackDevice ? payload.deviceKey : deviceKey,
                deviceGroupKey: useFallbackDevice ? payload.deviceGroupKey : metadata?.deviceGroupKey,
                devicePassword: useFallbackDevice ? payload.devicePassword : metadata?.devicePassword,
              });

              const keyToRemember = deviceKey ?? payload.deviceKey;
              if (payload.rememberDevice && keyToRemember) {
                return of(successAction, new RememberDevice({ deviceKey: keyToRemember, username }));
              }

              return of(successAction);
            }),
            catchError(error => of(new LogInMfaError({ error: error })))
          )
      )
    )
  );

  SsoLogInFailure: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.SSO_LOG_IN_FAILURE),
        tap(() => {
          this._router.navigateByUrl('/sso');
        })
      ),
    { dispatch: false }
  );

  LogInSuccess: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType(EAuthActions.LOG_IN_SUCCESS),
      map((action: LogInSuccess) => action.payload),
      switchMap(payload =>
        this._userService.getUserProfile().pipe(
          map(response => new FetchUserSuccess({ user: response, redirectToHome: true, redirectToBisa:  payload.isBisa })),
          catchError(error => of(new FetchUserFailure({ error: error })))
        )
      )
    )
  );

  SsoRedirect: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.SSO_REDIRECT),
        map((action: SsoRedirect) => action.payload),
        map(payload => {
          window.location.href = `${
            appConfig.cognito.uri
          }/oauth2/authorize?identity_provider=ADFS&response_type=code&client_id=${
            appConfig.cognito.adfsClientId
          }&redirect_uri=${window.location.origin}${appConfig.cognito.redirectPathname}`;
        })
      ),
    { dispatch: false }
  );

  InvalidSsoUserId: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.INVALID_SSO_USER_ID),
        tap(() => {
          this._router.navigateByUrl('/login');
        })
      ),
    { dispatch: false }
  );

  UnauthorizedUser: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.UNAUTHORIZED_USER),
        tap(() => {
          this._router.navigateByUrl('/unauthorized');
        })
      ),
    { dispatch: false }
  );

  FetchUserSuccess: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.FETCH_USER_SUCCESS),
        //ng15 investigate action of never? had to type as any.
        tap((action: any) => {
          window.dispatchEvent(new Event('FetchUserSuccess'));

          let redirectToUrl = this._route.snapshot.queryParams["redirect"];

          if(redirectToUrl){
            this._router.navigateByUrl(redirectToUrl);
          }
          else if (action.payload.redirectToBisa) {
            this._router.navigateByUrl('/bisa/home');
          } else if (action.payload.redirectToHome) {
            // Always navigate to home; remember device is handled inline in LogInMfa effect
            this._router.navigateByUrl('/home');
          }
        }),
      ),
    { dispatch: false },
  );

  Refresh: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<Refresh>(EAuthActions.REFRESH),
      switchMap(() => this._store.pipe(select(selectRefreshToken), take(1), filter(Boolean))),
      withLatestFrom(
        this._store.pipe(select(selectIsAuthenticated)),
        this._store.pipe(select(selectIsADFSLogin)),
        this._store.pipe(select(selectSessionDeviceKey))
      ),
      switchMap(([refreshToken, isAuthenticated, isADFSLogin, sessionDeviceKey]) =>
        this._userService.refresh({
          refresh: refreshToken,
          ...(!isADFSLogin && sessionDeviceKey ? { deviceKey: sessionDeviceKey } : {}),
        }, isADFSLogin).pipe(
          map(
            response =>
              new RefreshSuccess({
                accessToken: response.idToken,
                ttl: response.ttl,
              })
          ),
          catchError(error => {
            // log user out immediately, so they don't have to wait for all api requests to finish
            // don't throw an error if the user is not authenticated
            if (isAuthenticated) {
              return of(new LogOutWithError({ error: error }));
            }
            return of(new LogOut());
          })
        )
      )
    )
  );

  LogOut: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.LOG_OUT),
        tap((data: any) => {
          this._webSocketService.reset();
          this._router.navigateByUrl(data.payload === LABELS.MENU_TYPE_BISA ? BISACONSTANTS.bisaLogin : '/login');
        })
      ),
    { dispatch: false }
  );

  LogOutWithError: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.LOG_OUT_WITH_ERROR),
        tap(() => {
          this._webSocketService.reset();
          this._router.navigateByUrl('/login');
        })
      ),
    { dispatch: false }
  );

  RedirectToMfa: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.REDIRECT_TO_MFA),
        map((action: RedirectToMfa) => action.payload),
        tap((payload) => {
          const challengeName = payload.mfaChallengeInfo?.challengeName ?? null;
          const availableMethods = payload.mfaChallengeInfo?.availableMethods ?? [];

          // Only bail to home when there is truly nothing to do — no challenge and no methods.
          if (!challengeName && availableMethods.length === 0) {
            this._router.navigateByUrl('/home');
            return;
          }

          // If the backend already selected a method (challengeName set, no availableMethods list),
          // or only one method is available, go straight to the challenge screen.
          const goDirectToChallenge = availableMethods.length <= 1;

          const mfaRoute = goDirectToChallenge ? '/login/mfa' : '/login/mfa/select-method';
          const bisaMfaRoute = goDirectToChallenge ? '/bisa/login/mfa' : '/bisa/login/mfa/select-method';
          const route = payload.isBisa ? bisaMfaRoute : mfaRoute;

          let redirectToUrl = this._route.snapshot.queryParams["redirect"];
          if (redirectToUrl) {
            this._router.navigate([route], { queryParams: { redirect: `/${redirectToUrl}` }, state: { isBisa: payload.isBisa } });
          } else {
            this._router.navigateByUrl(route, { state: { isBisa: payload.isBisa } });
          }
        })
      ),
    { dispatch: false }
  );

  Lock: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.LOCK),
        tap(() => {
          this._router.navigateByUrl('/account-locked');
        })
      ),
    { dispatch: false }
  );

  AcceptCookies: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.ACCEPT_COOKIES),
        tap(() => {
          this._activityService
            .createActivityLog({ activityType: 'UserCookieAcceptance', key: 'IPAddress' })
            .subscribe();
        })
      ),
    { dispatch: false }
  );

  UpdatePassword: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdatePassword>(EAuthActions.UPDATE_PASSWORD),
      map((action: UpdatePassword) => action.payload),
      switchMap(payload =>
        this._userService.resetPassword(payload).pipe(
          map(() => new UpdatePasswordSuccess()),
          catchError(error => of(new UpdatePasswordFailure({ error: error })))
        )
      )
    )
  );

  UpdatePasswordSuccess: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.UPDATE_PASSWORD_SUCCESS),
        tap(() => {
          this._router.navigateByUrl('/login');
        })
      ),
    { dispatch: false }
  );

  FetchRouteRoleConfigsLoading: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType(EAuthActions.LOG_IN_SUCCESS),
      map(() => new FetchRouteConfigs())
    )
  );

  FetchRouteRoleConfigs: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType(EAuthActions.LOG_IN_SUCCESS),
      switchMap(() =>
        this._userService.getRolePages().pipe(
          map(
            (rolesPageCollection: RolesPageCollection) =>
              new FetchRouteConfigsSuccess({ rolesPages: rolesPageCollection.rolesPages })
          ),
          catchError(error => of(new FetchRouteConfigsFailure({ error: error })))
        )
      )
    )
  );

  UserProfileUpdated: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType(EUsersActions.UpdateUserProfileSuccess),
      switchMap(payload =>
        this._userService.getUserProfile().pipe(
          map(response => new FetchUserSuccess({ user: response, redirectToHome: false })),
          catchError(error => of(new FetchUserFailure({ error: error })))
        )
      )
    )
  );

  RefreshUserProfile: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType(EAuthActions.REFRESH_USER_PROFILE),
      switchMap(() =>
        this._userService.getUserProfile().pipe(
          map(response => new FetchUserSuccess({ user: response, redirectToHome: false })),
          catchError(error => of(new FetchUserFailure({ error: error })))
        )
      )
    )
  );

  FetchWebsocketTicket: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<FetchWebsocketTicket>(EAuthActions.FETCH_WEBSOCKET_TICKET),
      map((action: FetchWebsocketTicket) => action.payload),
      switchMap(payload =>
        this._ticketService.issueTicket({ token: payload.accessToken }).pipe(
          map((ticket: AuthTicket) => new FetchWebsocketTicketSuccess({ ticket })),
          catchError(error => of(new FetchWebsocketTicketFailure({ error: error })))
        )
      )
    )
  );

  SwitchMfaMethod$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<SwitchMfaMethod>(EAuthActions.SWITCH_MFA_METHOD),
      map((action: SwitchMfaMethod) => action.payload),
      exhaustMap(payload =>
        this._mfaService.switchMfaMethod({
          userId: String(payload.userId),
          sessionId: payload.sessionId,
          selectedMethod: payload.selectedMethod,
        }).pipe(
          map(response => new SwitchMfaMethodSuccess({
            sessionId: response.sessionId ?? '',
            challengeName: response.challengeName ?? '',
            deliveryDestination: response.deliveryDestination ?? '',
            availableMethods: response.availableMethods ?? [],
            phoneLastFour: response.phoneLastFour ?? null,
          })),
          catchError(error => of(new SwitchMfaMethodFailure({ error })))
        )
      )
    )
  );

  SwitchMfaMethodSuccess$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.SWITCH_MFA_METHOD_SUCCESS),
        tap(() => {
          this._router.navigateByUrl('/login/mfa');
        })
      ),
    { dispatch: false }
  );

  SelectLoginMfaMethod$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<SelectLoginMfaMethod>(EAuthActions.SELECT_LOGIN_MFA_METHOD),
      map((action: SelectLoginMfaMethod) => action.payload),
      exhaustMap(payload =>
        this._mfaService.selectMfaMethod({
          userId: Number(payload.userId),
          sessionId: payload.sessionId,
          selectedMethod: payload.selectedMethod,
          deviceKey: payload.deviceKey,
          deviceGroupKey: payload.deviceGroupKey,
          devicePassword: payload.devicePassword,
        }).pipe(
          map(response => new SelectLoginMfaMethodSuccess({
            sessionId: response.sessionId ?? '',
            challengeName: response.challengeName ?? '',
            deliveryDestination: response.deliveryDestination ?? '',
            availableMethods: response.availableMethods ?? [],
            phoneLastFour: response.phoneLastFour ?? null,
            deviceKey: response.deviceKey,
            deviceGroupKey: response.deviceGroupKey,
            devicePassword: response.devicePassword,
          })),
          catchError(error => of(new SelectLoginMfaMethodFailure({ error })))
        )
      )
    )
  );

  SelectLoginMfaMethodSuccess$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.SELECT_LOGIN_MFA_METHOD_SUCCESS),
        tap(() => {
          this._router.navigateByUrl('/login/mfa');
        })
      ),
    { dispatch: false }
  );

  InitiateTotpEnrollment$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<InitiateTotpEnrollment>(EAuthActions.INITIATE_TOTP_ENROLLMENT),
      withLatestFrom(
        this._store.pipe(select(selectTotpProvisioningUri)),
        this._store.pipe(select(selectTotpSecretCode)),
        this._store.pipe(select(selectTotpSession)),
        this._store.pipe(select(selectTotpEnrollAccessToken)),
        this._store.pipe(select(selectCognitoAccessToken)),
      ),
      exhaustMap(([, provisioningUri, secretCode, session, enrollAccessToken, cognitoAccessToken]) => {
        if (provisioningUri) {
          // Already enrolled in this session — reuse existing secret, do not call Cognito again
          return of(new InitiateTotpEnrollmentSuccess({
            provisioningUri,
            secretCode: secretCode ?? undefined,
            session: session ?? undefined,
            enrollAccessToken: enrollAccessToken ?? undefined,
          }));
        }
        // enrollTotp() requires authorization token parameter
        return this._mfaService.enrollTotp(cognitoAccessToken ?? '').pipe(
          map(response => new InitiateTotpEnrollmentSuccess({
            provisioningUri: response.provisioningUri ?? '',
            secretCode: response.secretCode,
            session: response.session,
            enrollAccessToken: cognitoAccessToken ?? undefined,
          })),
          catchError(error => of(new InitiateTotpEnrollmentFailure({ error })))
        );
      })
    )
  );

  VerifyTotpEnrollment$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<VerifyTotpEnrollment>(EAuthActions.VERIFY_TOTP_ENROLLMENT),
      map((action: VerifyTotpEnrollment) => action.payload),
      withLatestFrom(
        this._store.pipe(select(selectTotpSession)),
        this._store.pipe(select(selectTotpEnrollAccessToken)),
      ),
      switchMap(([payload, session, enrollAccessToken]) =>
        this._mfaService.verifyTotp({
          totpCode: payload.totpCode,
          session: session ?? undefined,
          accessToken: enrollAccessToken ?? undefined,
        }).pipe(
          map(() => new VerifyTotpEnrollmentSuccess()),
          catchError(error => of(new VerifyTotpEnrollmentFailure({ error })))
        )
      )
    )
  );

  FetchMfaOptions$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<FetchMfaOptions>(EAuthActions.FETCH_MFA_OPTIONS),
      switchMap(() =>
        this._mfaService.getMfaOptions().pipe(
          map(response => {
            // Handle both camelCase (actual backend) and PascalCase (package type definition) field names
            const raw = response as any;
            const mfaOptions = {
              isTotpEnabled: raw.isTotpEnabled ?? raw.IsTotpEnabled ?? false,
              isEmailOtpEnabled: raw.isEmailOtpEnabled ?? raw.IsEmailOtpEnabled ?? false,
              isSmsOtpEnabled: raw.isSmsOtpEnabled ?? raw.IsSmsOtpEnabled ?? false,
              isRememberDeviceEnabled: raw.isRememberDeviceEnabled ?? raw.RememberDeviceEnabled ?? false,
            };
            return new FetchMfaOptionsSuccess({ mfaOptions });
          }),
          catchError(error => of(new FetchMfaOptionsFailure({ error })))
        )
      )
    )
  );

  // Feature flag guard pattern: Use withLatestFrom to check feature flags before making API calls.
  // This prevents API calls when features are disabled, ensuring backend state is not modified.
  // The guard dispatches success without calling the API when disabled, maintaining consistent
  // action dispatch patterns. This approach is preferred over conditional action dispatch because:
  // 1. It keeps the effect logic centralized
  // 2. It ensures consistent success/failure handling
  // 3. It prevents reducer state mutations when features are disabled
  // 4. It's compatible with ngrx store hydration/persistence
  RememberDevice$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<RememberDevice>(EAuthActions.REMEMBER_DEVICE),
      map((action: RememberDevice) => action.payload),
      withLatestFrom(this._store.pipe(select(selectMfaOptions))),
      switchMap(([payload, mfaOptions]) => {
        // Only skip when feature is explicitly disabled; null means not-yet-loaded, so proceed
        if (mfaOptions?.isRememberDeviceEnabled === false) {
          return of(new RememberDeviceSuccess({ username: payload.username }));
        }
        // Feature enabled (or status unknown): proceed with API call
        // Bearer token is attached by the auth interceptor; stored device values are kept on failure.
        return this._mfaService.rememberDevice({ DeviceKey: payload.deviceKey }).pipe(
          map(() => new RememberDeviceSuccess({ username: payload.username })),
          catchError(error => of(new RememberDeviceFailure({ error })))
        );
      })
    )
  );

  RememberDeviceSuccess$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(EAuthActions.REMEMBER_DEVICE_SUCCESS),
        tap(() => this._router.navigateByUrl('/home'))
      ),
    { dispatch: false }
  );
}
